import React from 'react';
import { 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Box, 
  Card, 
  CardContent,
  Alert,
  AlertTitle,
  Divider
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BookIcon from '@mui/icons-material/Book';
import ChatIcon from '@mui/icons-material/Chat';
import PsychologyIcon from '@mui/icons-material/Psychology';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';

function Home() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Alert severity="info" sx={{ mb: 4 }}>
        <AlertTitle>Important Notice</AlertTitle>
        This information is for educational purposes only and should not replace professional medical advice. 
        Always consult with healthcare providers for personalized diagnosis and treatment.
      </Alert>
      
      <Grid container spacing={4}>
        {/* Hero Section */}
        <Grid item xs={12}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 6,
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(29, 161, 242, 0.1) 0%, rgba(138, 43, 226, 0.1) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              textAlign: 'center'
            }}
          >
            <Typography 
              variant="h2" 
              component="h1" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                color: 'primary.main',
                mb: 3
              }}
            >
              Welcome to Memory Loss Guide
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                color: 'text.primary',
                mb: 4,
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              Spreading awareness and providing accessible information about Alzheimer's disease, dementia, and memory loss
            </Typography>

            <Typography 
              variant="body1" 
              sx={{ 
                color: 'text.secondary',
                mb: 4,
                maxWidth: '700px',
                mx: 'auto',
                fontSize: '1.1rem',
                lineHeight: 1.8
              }}
            >
              Whether you're learning about Alzheimer's disease, supporting someone experiencing memory loss, 
              or seeking information, our platform offers a user-friendly way to access reliable resources, 
              connect with a supportive community, and get answers to your questions.
            </Typography>
          </Paper>
        </Grid>

        {/* Sign Up Alert */}
        {!isAuthenticated && (
          <Grid item xs={12}>
            <Alert 
              severity="info" 
              sx={{ 
                fontSize: '1rem',
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              <Typography variant="h6" gutterBottom>
                Sign Up Required
              </Typography>
              <Typography variant="body2">
                To access our blog feature and chat with our AI expert on memory loss, 
                please <strong>sign up</strong> for a free account. 
                Create your account today to start connecting with our community and accessing 
                valuable resources.
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  component={RouterLink}
                  to="/signup"
                  sx={{ mt: 1 }}
                >
                  Sign Up Now
                </Button>
                <Button 
                  variant="outlined" 
                  component={RouterLink}
                  to="/signin"
                  sx={{ mt: 1 }}
                >
                  Sign In
                </Button>
              </Box>
            </Alert>
          </Grid>
        )}

        {/* Features Section */}
        <Grid item xs={12}>
          <Typography 
            variant="h4" 
            component="h2"
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 4,
              textAlign: 'center',
              color: 'primary.main'
            }}
          >
            Our Features
          </Typography>
        </Grid>

        {/* Blog Feature */}
        <Grid item xs={12} md={6}>
          <Card 
            elevation={4}
            sx={{ 
              height: '100%',
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 8
              }
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BookIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography variant="h5" component="h3" sx={{ fontWeight: 600 }}>
                  Community Blog
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                Share your experiences, read stories from other families, and learn from 
                the community. Our blog platform allows you to connect with others who 
                understand the challenges of caring for loved ones with memory loss.
              </Typography>
              {!isAuthenticated && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Sign up required</strong> to access the blog feature.
                  </Typography>
                </Alert>
              )}
              {isAuthenticated && (
                <Button 
                  variant="contained" 
                  component={RouterLink}
                  to="/blog"
                  sx={{ mt: 2 }}
                  startIcon={<BookIcon />}
                >
                  Visit Blog
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Chat Feature */}
        <Grid item xs={12} md={6}>
          <Card 
            elevation={4}
            sx={{ 
              height: '100%',
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: 8
              }
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ChatIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography variant="h5" component="h3" sx={{ fontWeight: 600 }}>
                  AI Chat Assistant
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                Get instant answers to your questions about memory loss, Alzheimer's disease, 
                symptoms, treatments, and caregiving strategies. Our AI-powered chatbot is 
                an expert in memory loss and provides compassionate, evidence-based information 
                to help you navigate this journey.
              </Typography>
              {!isAuthenticated && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Sign up required</strong> to use the chat feature.
                  </Typography>
                </Alert>
              )}
              {isAuthenticated && (
                <Button 
                  variant="contained" 
                  component={RouterLink}
                  to="/ai-chat"
                  sx={{ mt: 2 }}
                  startIcon={<ChatIcon />}
                >
                  Start Chatting
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Purpose Section */}
        <Grid item xs={12}>
          <Divider sx={{ my: 4 }} />
          <Paper 
            elevation={2}
            sx={{ 
              p: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(29, 161, 242, 0.05) 0%, rgba(138, 43, 226, 0.05) 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <FamilyRestroomIcon sx={{ fontSize: 48, color: 'primary.main', mr: 2 }} />
              <Typography variant="h4" component="h2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Our Mission
              </Typography>
            </Box>
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: 'text.primary',
                mb: 2
              }}
            >
              <strong>Memory Loss Guide</strong> is dedicated to spreading awareness about 
              Alzheimer's disease, dementia, and memory loss. Our platform is designed for 
              anyone who wants to learn more about these conditions, whether you're personally 
              affected, know someone experiencing memory loss, or simply want to understand more 
              about these important health topics.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontSize: '1.1rem',
                lineHeight: 1.8,
                color: 'text.primary'
              }}
            >
              We provide a user-friendly platform that makes learning about Alzheimer's disease 
              accessible to everyone. Through our <strong>Alzheimer's knowledge base</strong>, 
              you can explore information about symptoms, diagnosis, treatments, and caregiving. 
              Our <strong>community blog</strong> allows people to share experiences and learn 
              from others, while our <strong>AI-powered chatbot</strong> offers instant, reliable 
              answers to your questions about memory loss and related conditions.
            </Typography>
          </Paper>
        </Grid>

        {/* Knowledge Base Feature */}
        <Grid item xs={12}>
          <Card 
            elevation={4}
            sx={{ 
              background: 'linear-gradient(135deg, rgba(29, 161, 242, 0.1) 0%, rgba(138, 43, 226, 0.1) 100%)',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PsychologyIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                <Typography variant="h5" component="h3" sx={{ fontWeight: 600 }}>
                  Alzheimer Knowledge Base
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.8 }}>
                Access comprehensive information about Alzheimer's disease, including symptoms, 
                diagnosis, treatment options, caregiving tips, and more. Our knowledge base is 
                freely accessible to help you understand and navigate memory loss concerns.
              </Typography>
              <Button 
                variant="outlined" 
                component={RouterLink}
                to="/alzheimer-knowledge"
                sx={{ mt: 1 }}
                startIcon={<PsychologyIcon />}
              >
                Explore Knowledge Base
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Copyright Footer */}
        <Grid item xs={12}>
          <Box
            sx={{
              mt: 6,
              pt: 4,
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Copyright @ Kyler Zhang 2025
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
}

export default Home; 